import { type IActionGameConfig, isTileVisibilitySampleDensity, type ITerraformingGameConfig, type TerraformingPresentationConfig } from '@nova/types';

import { parseObstacleTileStageConfig, parseObstructionProximityFadeSettings } from './parse-tf-presentation-config';
import terraformModeConfigJson from './terraform-mode.config.json';

/**
 * TF mode JSON before stage names are resolved to ObstacleTileStage.
 * Readable string stage fields stay in JSON without forcing typed presentation enums.
 */
type TerraformingModeConfigWire = {
  runtime?: Partial<IActionGameConfig['runtime']> & {
    navMeshHoleOffsetPx?: unknown;
  };
  presentation?: {
    tileRevealStagger?: TerraformingPresentationConfig['tileRevealStagger'];
    obstacleGrowth?: TerraformingPresentationConfig['obstacleGrowth'];
    obstructionProximityFade?: unknown;
    obstructionGrassSolidMinStage?: unknown;
    grassAimPreviewDelayMs?: unknown;
    localPlayerBot?: Partial<IActionGameConfig['presentation']['localPlayerBot']>;
  };
  tileVisibilityThreshold?: number;
  tileVisibilitySampleDensity?: number;
  seedPadBurstsUseTileVisibility?: boolean;
};

function readTerraformingPresentation(
  wirePresentation: TerraformingModeConfigWire['presentation'],
  mergedLocalPlayerBot: IActionGameConfig['presentation']['localPlayerBot']
): TerraformingPresentationConfig {
  if (!wirePresentation?.tileRevealStagger || !wirePresentation.obstacleGrowth) {
    throw new Error('terraform-mode.presentation requires tileRevealStagger and obstacleGrowth');
  }

  const grassAimPreviewDelayMs = wirePresentation.grassAimPreviewDelayMs;
  if (typeof grassAimPreviewDelayMs !== 'number' || !Number.isFinite(grassAimPreviewDelayMs) || grassAimPreviewDelayMs < 0) {
    throw new Error('terraform-mode.presentation.grassAimPreviewDelayMs must be a non-negative finite number');
  }

  return {
    localPlayerBot: mergedLocalPlayerBot,
    tileRevealStagger: wirePresentation.tileRevealStagger,
    obstacleGrowth: wirePresentation.obstacleGrowth,
    obstructionProximityFade: parseObstructionProximityFadeSettings(
      wirePresentation.obstructionProximityFade,
      'terraform-mode.presentation.obstructionProximityFade'
    ),
    obstructionGrassSolidMinStage: parseObstacleTileStageConfig(
      wirePresentation.obstructionGrassSolidMinStage,
      'terraform-mode.presentation.obstructionGrassSolidMinStage'
    ),
    grassAimPreviewDelayMs
  };
}

/** Builds the merged terraforming AGE game config from the shared action-game base. */
export function loadTerraformingGameConfig(base: IActionGameConfig): ITerraformingGameConfig {
  const wire: TerraformingModeConfigWire = terraformModeConfigJson;
  const { tileVisibilityThreshold, tileVisibilitySampleDensity, seedPadBurstsUseTileVisibility } = wire;

  if (typeof tileVisibilityThreshold !== 'number' || !Number.isFinite(tileVisibilityThreshold)) {
    throw new Error('terraform-mode.tileVisibilityThreshold must be a finite number');
  }

  if (!isTileVisibilitySampleDensity(tileVisibilitySampleDensity)) {
    throw new Error('terraform-mode.tileVisibilitySampleDensity must be 1, 4, 5, or 9');
  }

  if (typeof seedPadBurstsUseTileVisibility !== 'boolean') {
    throw new Error('terraform-mode.seedPadBurstsUseTileVisibility must be a boolean');
  }

  const navMeshHoleOffsetPx = wire.runtime?.navMeshHoleOffsetPx;
  if (typeof navMeshHoleOffsetPx !== 'number' || !Number.isFinite(navMeshHoleOffsetPx) || navMeshHoleOffsetPx < 0) {
    throw new Error('terraform-mode.runtime.navMeshHoleOffsetPx must be a non-negative finite number');
  }

  return {
    pxPerUnit: base.pxPerUnit,
    runtime: {
      ...base.runtime,
      ...wire.runtime,
      navMeshHoleOffsetPx
    },
    combat: base.combat,
    presentation: readTerraformingPresentation(
      wire.presentation,
      {
        ...base.presentation.localPlayerBot,
        ...wire.presentation?.localPlayerBot
      }
    ),
    tileVisibilityThreshold,
    tileVisibilitySampleDensity,
    seedPadBurstsUseTileVisibility
  };
}
